import { createTheme } from "@material-ui/core/styles";

const breakpointValues = {
    xs: 0,
    sm: 640,
    md: 1024,
    lg: 1200,
    xl: 1440
};

const palette = {
    primary: {
        main: "#422d93",
        light: '#5843ad',
        lighter: '#8470e0',
        accent: '#ECEAF4',
        kindaDark: '#3a2882',
        dark: '#211649',
    },
    secondary: {
        lightest: '#FDE8DD',
        light: '#ffa67c',
        main: "#F9661E",
        dark: '#ce5016'
    },
    grey: {
        lightest: '#EAEFF2',
        lighter: '#D9E0E4',
        light: '#D9E0E4',
        dark: '#D6D4DD',
        darker: '#9FA6B9',
        darkest: '#53585e',
        medium: '#9FA6B9',
    },
    status: {
        danger: "orange",
        error: "red",
        success: "green"
    },
    black: {
        main:"#222222"
    },
    error: {
        main: '#E62C2C'
    },
    warning: {
        main: '#b57119'
    },
    info: {
        main: '#F26724'
    },
    success: {
        main: '#009C77'
    },
    green: {
        main: '#009C77',
        light: '#72BDB0'
    },
    blue: {
        lightest: '#DAE1E5',
        lighter: '#CED2DE'
    },
    red: {
        lightest: '#F8DDD1',
    },
    white: {
        main: '#FFFFFF'
    },
    grayBackground: {
        main: '#E5E5E5',
        dark: '#bfbfbf',
        accent: '#EEEFF2'
    },
    contrastThreshold: 3,
    tonalOffset: 0.3,
    background: {
        default: '#F3F3F3'
    },
    nosh: {
        blue: '#3A4562',
        lightblue: '#EAF4F3',
        red: '#E97363',
        grey: '#EFE9E5'
    },
    button: {
        disabled: {
            // bgcolor:'#ABAEB3',
            // color: '#D5D7D9',
        },
    },
    text: {
        body: {
            main: '#272E35',
            subtle: '#888888',
            gray: '#F3F3F3'
        }
    }
};

const theme = createTheme({
    breakpoints: { values: breakpointValues },
    palette,
    transitions: {
        easing: {
            easeInOutSlow: "cubic-bezier(.27,.01,.81,1)"
        }
    },
    overrides: {},
    typography: {
        fontFamily: [
            'Arial',
            'sans-serif'
        ],
        fontSize: 16,
        button: {
            lineHeight: 1.166666,
        }
    }
});

const overrides = {
    MuiAutocomplete: {
        noOptions: {
            display: 'none'
        },
        popupIndicatorOpen: {
            transform: 'none'
        }
    },
    MuiAccordion: {
        root: {
            backgroundColor: 'white',
        }
    },
    MuiAccordionSummary: {
        root: {
            '&$expanded': {
                minHeight: 0
            }
        },
        content: {
            '&$expanded': {
                margin: '12px 0'
            }
        }
    },
    MuiTab: {
        root: {
            textTransform: 'none',
            color: theme.palette.text.body,
            fontWeight: 'bold'
        }
    },
    MuiSnackbarContent: {
        root: {
            backgroundColor: theme.palette.primary.main
        }
    },
    MuiCheckbox: {
        root: {
            // color: 'white',
        },
    },
    MuiButtonBase: {
        root: {
            fontSize: '1.125rem',
            borderRadius: '0.4em',
            transition: 'color 150ms, transform 150ms, border-color 150ms, background-color 150ms, box-shadow 150ms !important',
        }
    },
    MuiLink: {
        root: {
            fontWeight: 'bold',
            cursor: 'pointer',
        }
    },
    MuiMobileStepper: {
        root: {
            backgroundColor: 'transparent'
        },
        dot: {
            height: theme.spacing(2),
            width: theme.spacing(2),
            margin: `0 ${theme.spacing(1)}px`
        },
        dotActive: {
            backgroundColor: theme.palette.secondary.main
        }
    },
    MuiButton: {
        root: {
            textTransform: 'none',
            fontSize: '1.125rem',
            fontWeight: '600',
            borderRadius: '0.4em',
            outline: 'none !important',
            borderColor: palette.grey.light,
            lineHeight: 1.2,
            padding: '1rem 1rem',
            textAlign: 'center',
            textDecoration: 'none !important',
            "&$disabled": {
                // backgroundColor: palette.button.disabled.bgcolor,
                // color: palette.button.disabled.color,
                // borderColor: 'transparent !important'
            },
            '& .MuiSvgIcon-root': {
                fontSize: '26px !important'
            }
        },
        sizeLarge: {
            padding: '2.8vw 1rem',
            fontSize: '1.2rem',
            [theme.breakpoints.up('lg')]: {
                fontSize: '1.125rem',
                padding: '1.0em 2em',
            },
        },
        iconSizeLarge: {
            marginTop: `-${theme.spacing(1)}px`,
            marginBottom: `-${theme.spacing(1)}px`,
            '& > *:first-child': {
                fontSize: 30,
            }
        },
        outlined: {
            borderWidth: '2px !important',
            padding: "18px ​17px 17px",
            backgroundColor: 'white',
            borderColor: `${palette.grey.darker}`,
        },
        outlinedSizeSmall: {
            padding: '6px 9px 5px'
        },
        containedSizeSmall: {
            padding: '6px 9px 5px'
        },
        outlinedPrimary: {
            borderColor: `${palette.primary.main} !important`,
            '&:hover': {
                backgroundColor: `${palette.primary.main} !important`,
                color: 'white !important',
                boxShadow: 'none'
            },
            boxShadow: 'none',
            transition: 'color 150ms, background-color 150ms',
            fontSize:'22px'
        },
        outlinedSecondary: {
            color: `${palette.secondary.main} !important`,
            borderColor: `${palette.secondary.main} !important`,
            '&:hover': {
                backgroundColor: `${palette.secondary.main} !important`,
                color: 'white !important',
                boxShadow: 'none'
            },
            boxShadow: 'none',
            transition: 'color 150ms, background-color 150ms'
        },
        containedSecondary: {
            '&:hover': {
                backgroundColor: palette.secondary.dark,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2) !important'
            },
            backgroundColor: palette.secondary.main,
            // backgroundImage: `linear-gradient(177deg, rgba(0,0,0,0), 70%, rgba(0,0,0,0.2))`,
            transition: 'background-color 150ms',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            fontSize:'22px',
            "&$disabled": {
                backgroundColor: palette.secondary.dark,
                color: theme.palette.grey[300]
            }
        },
        textSizeSmall: {
            fontSize: '0.9375rem'
        },
        textSizeLarge: {
            fontSize: '1.375rem'
        },
        textSecondary: {
            '&:hover': {
                // backgroundColor: 'transparent',
                textDecoration: 'underline'
            }
        }
    },
    MuiAppBar: {
        positionSticky: {}
    },
    MuiDrawer: {
        paper: {
            zIndex: 2
        }
    },
    MuiTypography: {
        root: {
            "& + $h1": {
                marginTop: '100px'
            }
        },
        gutterBottom: {
            marginBottom: '1em'
        },
        body1: {
            "& + $h1, & + $h2, & + $h3, & + $h4, & + $h5, & + $h6": {
                marginTop: '1.5em'
            }
        },
        subtitle1: {
            fontSize: 14
        }
    },
    MuiOutlinedInput: {
        root: {
            backgroundColor: 'white',
            borderWidth: '2px'
        },
        notchedOutline: {
            borderWidth: '2px',
            borderColor: palette.grey.darker,
            borderRadius: '6px',
            '& legend': {
                width: 'auto'
            }
        },
        inputAdornedStart: {
            paddingLeft: theme.spacing(2)
        }
    },
    MuiFilledInput: {
        root: {
            backgroundColor: 'white !important',
            border: `solid 2px ${palette.grey.darker}!important`,
            borderRadius: '6px !important',
            paddingTop: '15px !important',
            '&:hover': {
                backgroundColor: 'white'
            }
        },
        input: {
            backgroundColor: 'white !important',
            border: `none !important`,
            borderRadius: '6px !important',
            boxShadow: 'none',
            paddingTop: '13px',
            '&:hover': {
                backgroundColor: 'white'
            }
        },
        // notchedOutline: {
        //     borderWidth: '1px',
        //     borderColor: palette.grey.light,
        //     borderRadius: '6px',
        //     '& legend': {
        //         width: 'auto'
        //     }
        // },
        underline: {
            '&:before': {
                display: 'none'
            },
            '&:after': {
                display: 'none'
            }
        },
        'inputMultiline': {
            paddingTop: '13px'
        }
        // focused: {
        //     backgroundColor: 'white'
        // }
    },
    MuiInputBase: {
        root: {
            // borderRadius: '6px !important',
            // borderWidth: '2px !important'
        },
        input: {
            // height: 'auto',
            // boxShadow: 'none !important',
            // borderRadius: '6px'
        },
    },
    MuiSvgIcon: {
        root: {
            fontSize: '1.4rem'
        }
    },
    MuiSwitchBase: {
        root: {
            padding: theme.spacing(2)
        }
    },
    MuiDialog: {
        paper: {
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '1.2rem 2rem'
        }
    },
    MuiDialogTitle: {
        root: {
            position: 'relative',
            zIndex: 1,
            padding: 0
        },
    },
    MuiDialogContent: {
        root: {
            padding: '1.5rem 0 0'
        }
    },
    MuiPaper: {
        outlined: {
            backgroundColor: 'white',
        },
        rounded: {
            borderRadius: `${theme.spacing(1)}px`
        }
    },
    MuiListItemText: {
        dense: {
            margin: 0
        }
    },
    MuiMenuItem: {
        root: {
            borderRadius: 0
        }
    },
    MuiFormGroup: {
        root: {
            '& .MuiFormControlLabel-root': {
                // marginBottom: '1rem'
            }
        }
    },
    MuiFormControlLabel: {
        root: {
            // border: `solid 1px ${palette.grey.medium}`,
            // borderWidth: '2px',
            borderRadius: '0.4em',
            transition: 'border-color 150ms',
            marginLeft: 0,
            paddingRight: theme.spacing(2),
        },
        disabled: {
            borderColor: theme.palette.text.disabled
        }
    }
};

theme.overrides = {
    ...theme.overrides,
    ...overrides
};

const typography = {
    h1: {
        fontSize: 30,
        fontWeight: 400,
        [theme.breakpoints.up('md')]: {
        },
        [theme.breakpoints.up('lg')]: {
        }
    },
    h2: {
        fontSize: 30,
        fontWeight: 800,
        lineHeight: 1.17
    },
    h3: {
        fontSize: 20,
        fontWeight: 400,
        marginBottom: theme.spacing(3),
        [theme.breakpoints.up('lg')]: {
        },
    },
    h4: {
        fontSize: 26,
        fontWeight: 800,
        [theme.breakpoints.up('lg')]: {
        },
    },
    h5: {
        fontSize: 24,
        fontWeight: 800,
        [theme.breakpoints.up('lg')]: {
        },
    },
    h6: {
        fontSize: 22,
        fontWeight: 800,
        [theme.breakpoints.up('lg')]: {
        },
    }
};

theme.typography = {
    ...theme.typography,
    ...typography
};

export {
    typography,
    breakpointValues,
    palette,
    overrides
};


export default theme;